import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ControllerRenderProps, FormProvider, useForm } from 'react-hook-form';

import {
  type DashboardUserPlatformModeSettings,
  NotificationCategory,
  NotificationType,
  dashboardUserGetDashboardUserPlatformModeSettingsOptions,
  dashboardUserGetDashboardUserPlatformModeSettingsQueryKey,
  dashboardUserUpdateDashboardUserPlatformModeSettingsMutation,
} from '@column/api';
import { Button } from '@column/column-ui-kit';
import { Checkbox, Field, FieldContent, FieldDescription, FieldGroup, FieldLabel } from '@column/ui';

import { PageActions } from '~/app/Layout/PageFrameContext';
import { Loading, RenderFields } from '~/components';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';

type NotificationGroupKey = 'outgoing' | 'incoming';

interface NotificationCategoryConfig {
  category: NotificationCategory;
  description: string;
  label: string;
}

interface NotificationGroup {
  key: NotificationGroupKey;
  heading: string;
  categories: NotificationCategoryConfig[];
}

const NOTIFICATION_CATEGORY_CONFIGS: Record<NotificationCategory, NotificationCategoryConfig> = {
  [NotificationCategory.TRANSFER_OUTGOING_PENDING_APPROVAL]: {
    category: NotificationCategory.TRANSFER_OUTGOING_PENDING_APPROVAL,
    label: 'Pending Approval',
    description: 'A transfer is waiting for approval before it can be sent.',
  },
  [NotificationCategory.TRANSFER_OUTGOING_INITIATED]: {
    category: NotificationCategory.TRANSFER_OUTGOING_INITIATED,
    label: 'Initiated',
    description: 'A transfer has been created.',
  },
  [NotificationCategory.TRANSFER_OUTGOING_COMPLETED]: {
    category: NotificationCategory.TRANSFER_OUTGOING_COMPLETED,
    label: 'Completed',
    description: 'A transfer has completed.',
  },
  [NotificationCategory.TRANSFER_OUTGOING_RETURNED]: {
    category: NotificationCategory.TRANSFER_OUTGOING_RETURNED,
    label: 'Returned',
    description: 'A transfer has been returned.',
  },
  [NotificationCategory.TRANSFER_INCOMING_RECEIVED]: {
    category: NotificationCategory.TRANSFER_INCOMING_RECEIVED,
    label: 'Received',
    description: 'A transfer has been received.',
  },
  [NotificationCategory.TRANSFER_INCOMING_PENDING_RETURN]: {
    category: NotificationCategory.TRANSFER_INCOMING_PENDING_RETURN,
    label: 'Pending Return',
    description: 'A transfer may be returned.',
  },
  [NotificationCategory.TRANSFER_INCOMING_RETURNED]: {
    category: NotificationCategory.TRANSFER_INCOMING_RETURNED,
    label: 'Returned',
    description: 'A transfer has been returned.',
  },
};

const NOTIFICATION_GROUPS: NotificationGroup[] = [
  {
    key: 'outgoing',
    heading: 'Outgoing Transfers',
    categories: [
      NOTIFICATION_CATEGORY_CONFIGS[NotificationCategory.TRANSFER_OUTGOING_PENDING_APPROVAL],
      NOTIFICATION_CATEGORY_CONFIGS[NotificationCategory.TRANSFER_OUTGOING_INITIATED],
      NOTIFICATION_CATEGORY_CONFIGS[NotificationCategory.TRANSFER_OUTGOING_COMPLETED],
      NOTIFICATION_CATEGORY_CONFIGS[NotificationCategory.TRANSFER_OUTGOING_RETURNED],
    ],
  },
  {
    key: 'incoming',
    heading: 'Incoming Transfers',
    categories: [
      NOTIFICATION_CATEGORY_CONFIGS[NotificationCategory.TRANSFER_INCOMING_RECEIVED],
      NOTIFICATION_CATEGORY_CONFIGS[NotificationCategory.TRANSFER_INCOMING_PENDING_RETURN],
      NOTIFICATION_CATEGORY_CONFIGS[NotificationCategory.TRANSFER_INCOMING_RETURNED],
    ],
  },
];

const SAVE_ACTION_EXIT_DURATION_MS = 150;

type NotificationGroupValues = Partial<Record<NotificationCategory, boolean>>;
type NotificationFormValues = Record<NotificationGroupKey, NotificationGroupValues>;

const getDefaultFormValues = (): NotificationFormValues =>
  NOTIFICATION_GROUPS.reduce((acc, group) => {
    acc[group.key] = group.categories.reduce((groupAcc, { category }) => {
      groupAcc[category] = false;
      return groupAcc;
    }, {} as NotificationGroupValues);
    return acc;
  }, {} as NotificationFormValues);

const getFormValues = (settings?: DashboardUserPlatformModeSettings): NotificationFormValues => {
  const values = getDefaultFormValues();
  const categories = settings?.notifications?.categories ?? {};

  NOTIFICATION_GROUPS.forEach((group) => {
    group.categories.forEach(({ category }) => {
      values[group.key][category] = categories[category]?.[NotificationType.EMAIL]?.enabled ?? false;
    });
  });

  return values;
};

const getRequestFromFormValues = (values: NotificationFormValues): DashboardUserPlatformModeSettings => ({
  notifications: {
    categories: Object.fromEntries(
      NOTIFICATION_GROUPS.flatMap((group) =>
        group.categories.map(({ category }) => [
          category,
          {
            [NotificationType.EMAIL]: {
              enabled: values[group.key]?.[category] ?? false,
            },
          },
        ])
      )
    ),
  },
});

export const PageNotifications: React.FC = () => {
  const { currentPlatform, currentUser } = useSessionStore();
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const queryClient = useQueryClient();
  const platformId = currentPlatform?.id ?? currentUser?.defaultPlatformId;

  const platformModeSettingsQuery = useQuery({
    ...dashboardUserGetDashboardUserPlatformModeSettingsOptions(),
    enabled: !!currentUser && !!platformId,
  });
  const updatePlatformModeSettings = useMutation(dashboardUserUpdateDashboardUserPlatformModeSettingsMutation());

  const formMethods = useForm<NotificationFormValues>({ defaultValues: getDefaultFormValues() });
  const { formState, handleSubmit, reset } = formMethods;

  const [isSaveActionExiting, setIsSaveActionExiting] = useState(false);
  const saveActionExitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearSaveActionExitTimeout = useCallback(() => {
    if (saveActionExitTimeoutRef.current) {
      clearTimeout(saveActionExitTimeoutRef.current);
      saveActionExitTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => clearSaveActionExitTimeout, [clearSaveActionExitTimeout]);

  useEffect(() => {
    reset(getFormValues(platformModeSettingsQuery.data));
  }, [platformModeSettingsQuery.data, reset]);

  const isNotificationInputDisabled = updatePlatformModeSettings.isPending || !platformId;

  const handleSave = handleSubmit(async (values) => {
    try {
      await updatePlatformModeSettings.mutateAsync({
        body: getRequestFromFormValues(values),
      });
      clearSaveActionExitTimeout();
      setIsSaveActionExiting(true);
      reset(values);
      saveActionExitTimeoutRef.current = setTimeout(() => {
        setIsSaveActionExiting(false);
        saveActionExitTimeoutRef.current = null;
      }, SAVE_ACTION_EXIT_DURATION_MS);
      await queryClient.invalidateQueries({
        queryKey: dashboardUserGetDashboardUserPlatformModeSettingsQueryKey(),
      });
      addSuccessNotification({
        content: 'Notification settings saved',
      });
    } catch (error) {
      addDangerNotification({
        content: error instanceof Error ? error.message : 'Failed to save notification settings',
      });
    }
  });

  const shouldShowSaveAction = formState.isDirty || isSaveActionExiting;

  return (
    <>
      {shouldShowSaveAction && (
        <PageActions>
          <div
            className={`transition-[opacity,transform] duration-150 ease-[cubic-bezier(0.25,0.1,0.25,1)] ${
              isSaveActionExiting
                ? 'pointer-events-none translate-x-2 scale-95 opacity-0'
                : 'translate-x-0 scale-100 opacity-100'
            }`}
          >
            <Button
              isDisabled={!platformId || updatePlatformModeSettings.isPending || isSaveActionExiting}
              isLoading={updatePlatformModeSettings.isPending}
              onClick={handleSave}
              size="small"
            >
              Save changes
            </Button>
          </div>
        </PageActions>
      )}
      <Loading isLoading={platformModeSettingsQuery.isLoading}>
        <FormProvider {...formMethods}>
          {RenderFields<NotificationFormValues>({
            sections: [
              {
                headline: 'Email Notifications',
                description: 'Configure which notifications will be sent to your email.',
                fields: NOTIFICATION_GROUPS.map((group) => ({
                  id: group.key,
                  label: group.heading,
                  children: ({ value, onChange }: ControllerRenderProps<NotificationFormValues>) => {
                    const groupValue = (value ?? {}) as NotificationGroupValues;

                    return (
                      // The first item's label baseline is aligned with the field Label by a
                      // convention in FormField (RenderFields), so no per-instance offset here.
                      <FieldGroup variant="outline">
                        {group.categories.map(({ category, description, label }) => {
                          const checkboxId = `notification-${category}`;

                          return (
                            <Field key={category} orientation="horizontal">
                              <Checkbox
                                checked={groupValue[category] ?? false}
                                disabled={isNotificationInputDisabled}
                                id={checkboxId}
                                onCheckedChange={(checked) => onChange({ ...groupValue, [category]: checked === true })}
                              />
                              <FieldContent>
                                <FieldLabel htmlFor={checkboxId}>{label}</FieldLabel>
                                <FieldDescription className="text-xs">{description}</FieldDescription>
                              </FieldContent>
                            </Field>
                          );
                        })}
                      </FieldGroup>
                    );
                  },
                })),
              },
            ],
          })}
        </FormProvider>
      </Loading>
    </>
  );
};
